import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { XCircle, ListFilter, ChevronLeft, ChevronRight } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LeaveDetailModal } from "./LeaveDetailModal";
import { LeaveApprovalButtons } from "./LeaveApprovalButtons";
import { useAuth } from "@/contexts/AuthContext";
import { apiCancelLeaveRequest, type LeaveRequest, type LeaveTypeConfig } from "@/lib/api";
import { useDateLocale } from "@/hooks/useDateLocale";
import { getLeaveApiErrorMessage, getLeaveStatusLabel, LEAVE_STATUS_FILTERS } from "@/lib/leave-i18n";

const STATUS_CONFIG: Record<string, { dot: string; text: string; pill: string }> = {
  Approved: {
    dot: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-400",
    pill: "bg-emerald-50 border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/20",
  },
  Pending: {
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-400",
    pill: "bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/20",
  },
  "Pending Employee Document": {
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-400",
    pill: "bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/20",
  },
  "Document Uploaded": {
    dot: "bg-blue-500",
    text: "text-blue-700 dark:text-blue-400",
    pill: "bg-blue-50 border border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/20",
  },
  "HR Review Required": {
    dot: "bg-blue-500",
    text: "text-blue-700 dark:text-blue-400",
    pill: "bg-blue-50 border border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/20",
  },
  Rejected: {
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-400",
    pill: "bg-red-50 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20",
  },
  Cancelled: {
    dot: "bg-gray-400",
    text: "text-gray-600 dark:text-gray-400",
    pill: "bg-gray-100 border border-gray-200 dark:bg-gray-800/50 dark:border-gray-700",
  },
};

function StatusBadge({ status, label }: { status: string; label: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.Cancelled;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.pill} ${cfg.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {label}
    </span>
  );
}

function fmt(dateStr: string, locale: Locale) {
  try {
    return format(new Date(dateStr), "d MMM yyyy", { locale });
  } catch {
    return dateStr;
  }
}

interface LeaveListViewProps {
  requests: LeaveRequest[];
  leaveTypes: LeaveTypeConfig[];
  showEmployee?: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1];
const PAGE_SIZE = 8;

export function LeaveListView({
  requests,
  leaveTypes,
  showEmployee = false,
}: LeaveListViewProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { user } = useAuth();

  const [yearFilter, setYearFilter] = useState<string>(String(CURRENT_YEAR));
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [detailRequest, setDetailRequest] = useState<LeaveRequest | null>(null);
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const isAdminOrHr =
    user?.role.mainRole === "admin" || user?.role.mainRole === "hr";
  const isLineManager = !!(user?.isLineManager || user?.role.subRole === "line_manager");
  const canApprove = isAdminOrHr || isLineManager;

  const cancelMutation = useMutation({
    mutationFn: (id: number) => apiCancelLeaveRequest(id),
    onSuccess: () => {
      toast.success(t("leave.requestCancelled"));
      queryClient.invalidateQueries({ queryKey: ["myLeaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["myLeaveBalance"] });
    },
    onError: (err: Error) => toast.error(getLeaveApiErrorMessage(t, err)),
  });

  // Apply year + type filters first so chip counts reflect the current year/type scope
  const yearTypeFiltered = requests.filter((r) => {
    if (yearFilter !== "all" && new Date(r.startDate).getFullYear() !== Number(yearFilter)) return false;
    if (typeFilter !== "all" && r.leaveType !== typeFilter) return false;
    return true;
  });

  // Statuses that actually appear (to avoid showing empty chips)
  const presentStatuses = LEAVE_STATUS_FILTERS.filter((s) =>
    yearTypeFiltered.some((r) => r.status === s),
  );

  const filtered = yearTypeFiltered.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [yearFilter, typeFilter, statusFilter]);

  return (
    <div className="space-y-3">
      {/* Status filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {(["all", ...presentStatuses] as const).map((s) => {
          const count = s === "all" ? yearTypeFiltered.length : yearTypeFiltered.filter((r) => r.status === s).length;
          const active = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              }`}
            >
              <span>{s === "all" ? t("common.all") : getLeaveStatusLabel(t, s)}</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none ${active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border/50 bg-muted/30">
        <ListFilter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium text-muted-foreground mr-1">{t("leave.filter")}</span>

        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="h-7 text-xs px-2.5 w-auto min-w-[80px] bg-background border-border/60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("leave.allYears")}</SelectItem>
            {YEAR_OPTIONS.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-7 text-xs px-2.5 w-auto min-w-[110px] bg-background border-border/60">
            <SelectValue placeholder={t("leave.filterType")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("leave.filterType")}</SelectItem>
            {leaveTypes.map((lt) => (
              <SelectItem key={lt.name} value={lt.name}>
                {t(`leave.types.${lt.name}`, { defaultValue: lt.label })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filtered.length > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {t("leave.requestCount", { count: filtered.length })}
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="py-14 text-center text-sm text-muted-foreground">
          {t("leave.noMatchingRequests")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border/50 hover:bg-transparent">
                  {showEmployee && (
                    <TableHead className="text-xs font-semibold text-muted-foreground">
                      {t("leave.employee")}
                    </TableHead>
                  )}
                  <TableHead className="text-xs font-semibold text-muted-foreground">
                    {t("leave.leaveType")}
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground">
                    {t("leave.period")}
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground text-center">
                    {t("leave.days")}
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground">
                    {t("leave.status")}
                  </TableHead>
                  {showEmployee && (
                    <TableHead className="text-xs font-semibold text-muted-foreground">
                      {t("leave.coverage")}
                    </TableHead>
                  )}
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((r) => (
                  <TableRow
                    key={r.id}
                    className="border-b border-border/40 hover:bg-muted/40 transition-colors cursor-pointer"
                    onClick={() => setDetailRequest(r)}
                  >
                    {showEmployee && (
                      <TableCell className="font-medium text-sm whitespace-nowrap py-3">
                        {r.employeeName}
                      </TableCell>
                    )}
                    <TableCell className="py-3">
                      <span className="flex items-center gap-2 whitespace-nowrap text-sm">
                        {r.leaveTypeColor && (
                          <span
                            className="inline-block h-2 w-2 rounded-full shrink-0"
                            style={{ backgroundColor: r.leaveTypeColor }}
                          />
                        )}
                        {t(`leave.types.${r.leaveType}`, {
                          defaultValue: r.leaveTypeLabel ?? r.leaveType,
                        })}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground py-3">
                      {fmt(r.startDate, dateLocale)}
                      {r.startDate !== r.endDate && <> - {fmt(r.endDate, dateLocale)}</>}
                    </TableCell>
                    <TableCell className="text-center text-sm font-medium py-3">
                      {r.workingDays}
                    </TableCell>
                    <TableCell className="py-3">
                      <StatusBadge status={r.status} label={getLeaveStatusLabel(t, r.status)} />
                    </TableCell>
                    {showEmployee && (
                      <TableCell className="text-sm text-muted-foreground py-3">
                        {r.replacementEmployeeName ?? "-"}
                      </TableCell>
                    )}
                    <TableCell className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {canApprove &&
                          showEmployee &&
                          ["Pending", "HR Review Required", "Document Uploaded"].includes(r.status) && (
                          <LeaveApprovalButtons requestId={r.id} employeeId={r.employeeId} />
                        )}
                        {!showEmployee && r.status === "Pending" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            title={t("leave.cancelRequest")}
                            disabled={cancelMutation.isPending}
                            onClick={() => cancelMutation.mutate(r.id)}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/50 bg-muted/20">
              <span className="text-xs text-muted-foreground">
                {t("leave.pageSummary", {
                  page: safePage,
                  totalPages,
                  count: filtered.length,
                })}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <Button
                    key={p}
                    variant={p === safePage ? "default" : "ghost"}
                    size="sm"
                    className={`h-7 w-7 p-0 text-xs ${p === safePage ? "" : "text-muted-foreground"}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <LeaveDetailModal
        request={detailRequest}
        open={!!detailRequest}
        onOpenChange={(v) => {
          if (!v) setDetailRequest(null);
        }}
      />
    </div>
    </div>
  );
}
